import { reaction } from "mobx";
import _ from "lodash";
import { subscribe, unsubscribe } from "../../../../../util/events";
import { setupStickyHeader } from "./sticky-header";

import documentStore from "@app/state/store/report-document/document";
import report from "@app/state/store/report";
import { parseCaptionText } from "@app/components/report-document/editor/modules/diff/diff-utility";

export class TableView {
    constructor({ editor, node, view, getPos, cellMinWidth, tables, isEditable }) {
        this.editor = editor;
        this.node = node;
        this.id = node.attrs.id;
        this.cellMinWidth = cellMinWidth;
        this.dom = document.createElement("div");
        this.dom.className = "tableWrapper";
        this.index = tables.findIndex((table) => table.id === node.attrs.id);
        this.isEditable = isEditable;
        this.table = this.dom.appendChild(document.createElement("table"));
        this.colgroup = this.table.appendChild(document.createElement("colgroup"));
        this.caption = this.table.appendChild(document.createElement("caption"));
        this.contentDOM = this.table.appendChild(document.createElement("tbody"));
        this.dom.setAttribute("data-table-id", node.attrs.id);

        const change = node.attrs["data-diff-node"];
        const diffId = node.attrs["data-diff-id"];

        if (change) {
            this.dom.classList.add(`diff-${change}`);
            this.dom.setAttribute("data-diff-id", diffId);
        }

        // Check if this table is nested (inside another table cell)
        const pos = getPos();
        this.isNested = this._checkIfNested(editor, pos);

        if (this.isNested) {
            this.dom.classList.add("nested-table");
            this.table.classList.add("nested");
        }

        // check the parent to determine the landscape/portrait mode
        const slice = editor.state.doc.slice(pos, pos + 1, true);
        const parent = slice.content.firstChild;
        if (parent.type.name === "landscape") {
            this.table.className = "landscape";
        }

        // Note: Tables in lists are now allowed, removed the "invalid" class
        // This allows tables to be properly rendered within list items

        this.onScrollToTable = (e) => {
            const tableId = e.detail;

            if (tableId === node.attrs.id) {
                this.dom.scrollIntoView({ behavior: "smooth" });
            }
        };

        this.updateSection = () => {
            if (editor.sectionId) {
                const content = editor.getJSON();
                documentStore.processSection(editor.sectionId, content);
            }
        };

        // listen for store table change to update the caption
        this.reactionCleanup = reaction(
            () => documentStore.tables,
            () => updateTableCaption(this),
            { delay: 200 },
        );

        subscribe("scrollToNode", this.onScrollToTable);

        updateTableCaption(this);
        updateColumns(node, this.colgroup, this.table, cellMinWidth);

        // Freeze the header row for non-nested tables; defer a frame so the DOM is attached.
        if (!this.isNested) {
            this.stickyHeaderFrame = requestAnimationFrame(() => {
                this.stickyHeaderFrame = null;
                this.stickyCleanup = setupStickyHeader(this.table, {
                    manageWrapperOverflow: true,
                });
            });
        }
    }

    update(node) {
        if (node.type !== this.node.type) {
            return false;
        }

        if (node.attrs.caption !== this.node.attrs.caption) {
            documentStore.updateTableCaption(this.id, node.attrs.caption);
            this.updateSection();
        }

        this.node = node;

        updateTableCaption(this);
        updateColumns(node, this.colgroup, this.table, this.cellMinWidth);
        return true;
    }

    ignoreMutation(record) {
        // this.dom: the sticky-header overflow toggle mutates the wrapper; without this the
        // editable view keeps recreating the node view in a loop and the header never pins.
        return (
            record.type === "attributes" &&
            (record.target === this.dom ||
                record.target === this.table ||
                this.colgroup.contains(record.target))
        );
    }

    destroy() {
        unsubscribe("scrollToNode", this.onScrollToTable);
        this.reactionCleanup();

        if (this.stickyHeaderFrame) {
            cancelAnimationFrame(this.stickyHeaderFrame);
        }
        if (this.stickyCleanup) {
            this.stickyCleanup();
        }
    }

    /**
     * Check if this table is nested inside another table cell
     */
    _checkIfNested(editor, pos) {
        try {
            const $pos = editor.state.doc.resolve(pos);

            // Walk up the document tree to find if we're inside a table cell
            for (let depth = $pos.depth; depth >= 0; depth--) {
                const node = $pos.node(depth);

                if (node && (node.type.name === "tableCell" || node.type.name === "tableHeader")) {
                    return true;
                }
            }

            return false;
        } catch (e) {
            return false;
        }
    }
}

function updateTableCaption({ id, table, isEditable, caption, node, isNested }) {
    const captionData = documentStore.tableDetails(id) ?? {};

    if (isEditable) {
        caption.classList.add("editable");
    }

    // Don't show captions for nested tables
    if (isNested || captionData.isNested) {
        caption.innerHTML = "";
        caption.style.display = "none";
        return;
    }

    caption.style.display = "";

    // Apply caption placement based on document style config
    const placement = report.config?.reportDocuments?.style?.tableCaptionPlacement || "above";

    // Remove all placement classes first
    table.classList.remove("caption-above", "caption-below", "caption-inside-header");

    if (placement === "below") {
        table.classList.add("caption-below");
    } else if (placement === "insideHeader") {
        table.classList.add("caption-inside-header");
    } else {
        table.classList.add("caption-above");
    }

    const nodeCaption = String(node.attrs.caption ?? "");
    if (!_.isEmpty(nodeCaption)) {
        // Use fullCaption from store if available, otherwise use "Table: [caption]" for preview
        const captionText = captionData.fullCaption || `Table: ${nodeCaption}`;
        caption.innerHTML = parseCaptionText(captionText);
    } else {
        caption.innerHTML = "";
    }
}

export function updateColumns(node, colgroup, table, cellMinWidth, overrideCol, overrideValue) {
    let nextDOM = colgroup.firstChild,
        row = node.firstChild,
        updatedColValType = null,
        updatedColVal = 0;

    if (!row) {
        return;
    }

    for (let i = 0, col = 0; i < row.childCount; i++) {
        let { colspan, colwidth } = row.child(i).attrs;
        for (let j = 0; j < colspan; j++, col++) {
            let hasWidth = overrideCol === col ? overrideValue : colwidth && colwidth[j];
            let _colwidth = colwidth ? colwidth[j] : cellMinWidth;

            if (overrideCol === col) {
                if (_colwidth >= hasWidth) {
                    updatedColValType = "ADD";
                    updatedColVal = _colwidth - hasWidth;
                } else {
                    updatedColValType = "DELETE";
                    updatedColVal = hasWidth - _colwidth;
                }
            }

            if (overrideCol + 1 === col) {
                if (updatedColValType === "ADD") {
                    hasWidth += updatedColVal;
                } else {
                    hasWidth -= updatedColVal;
                }
            }

            let cssWidth = hasWidth ? hasWidth + "px" : "";

            if (!nextDOM) {
                colgroup.appendChild(document.createElement("col")).style.width = cssWidth;
            } else {
                if (cssWidth.length && nextDOM.style.width !== cssWidth) {
                    nextDOM.style.width = cssWidth;
                }
                nextDOM = nextDOM.nextSibling;
            }
        }
    }

    while (nextDOM) {
        let after = nextDOM.nextSibling;
        nextDOM.parentNode.removeChild(nextDOM);
        nextDOM = after;
    }

    // table.style.width = "100%";
    // table.style.minWidth = "650px";
}
